import React, { useState } from 'react';

import { Button, Group, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { useLocalization } from 'context/localization-context';
import useDiscountByCode from 'hooks/discount/useDiscountByCode';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { DiscountRetrieve } from 'types';
import { computeDiscountAmount } from 'utils/discount.helpers';

interface IDiscountCodeInputProps {
  athleteId?: string;
  salesPortalGym?: string;
  productId?: string;
  value: DiscountRetrieve | null;
  onChange: (discount: DiscountRetrieve | null) => void;
  packageTotalCents: number;
}

const DiscountCodeInput = ({
  athleteId,
  salesPortalGym,
  productId,
  value,
  onChange,
  packageTotalCents,
}: IDiscountCodeInputProps): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency } = useLocalization();
  const { trackDiscountCodeApplied } = useTracking();

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { refetch, isFetching } = useDiscountByCode({
    code: code.trim(),
    athleteId,
    productId,
    salesPortalGym,
  });

  const appliedTo: 'athlete_app' | 'sales_portal' = salesPortalGym ? 'sales_portal' : 'athlete_app';

  const isApplied = !!value;
  const displayCode = isApplied ? (value as DiscountRetrieve).code : code;
  const savedCents = isApplied ? computeDiscountAmount(packageTotalCents, value as DiscountRetrieve) : 0;

  const handleApply = async (): Promise<void> => {
    if (!code.trim()) return;

    const result = await refetch();
    if (result.error) {
      setErrorMessage('Invalid Code. Verify your code and try again.');
    } else if (result.data) {
      const discount = result.data.data;
      onChange(discount);
      setErrorMessage(null);
      trackDiscountCodeApplied(
        discount.discountId,
        discount.name,
        discount.code,
        discount.valueType,
        appliedTo,
      );
    }
  };

  const handleClear = (): void => {
    onChange(null);
    setCode('');
    setErrorMessage(null);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCode(e.target.value);
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  return (
    <Stack spacing={4} mt='md'>
      <Stack mb='sm' spacing={0}>
        <Text align='center' color='dimmed' size='sm'>
          Have a discount code? Apply it here to unlock your special pricing!{' '}
        </Text>
        <Text align='center' color='dimmed' size='sm'>
          (Applies to first installment only.)
        </Text>
      </Stack>
      <Group spacing='xs' noWrap align='flex-start'>
        <TextInput
          placeholder='Discount code'
          value={displayCode}
          onChange={handleCodeChange}
          onKeyDown={e => {
            if (e.key === 'Enter' && !isApplied) {
              e.preventDefault();
              handleApply();
            }
          }}
          readOnly={isApplied}
          error={!!errorMessage}
          styles={isApplied ? { input: { borderColor: theme.colors.green[6] } } : undefined}
          style={{ flex: 1 }}
        />
        {!isApplied ? (
          <Button
            onClick={isApplied ? handleClear : handleApply}
            loading={isFetching}
            disabled={!isApplied && !code.trim()}
            aria-label={'Apply discount'}
          >
            Apply
          </Button>
        ) : (
          <Button
            onClick={isApplied ? handleClear : handleApply}
            loading={isFetching}
            disabled={!isApplied && !code.trim()}
            aria-label={'Remove discount'}
            variant='subtle'
          >
            <IconX color='gray' />
          </Button>
        )}
      </Group>
      {isApplied && (
        <Text size='sm' color='green' mt='sm'>
          Code Applied! You saved {formatCurrency(savedCents / 100)}
        </Text>
      )}
      {errorMessage && (
        <Text size='sm' color='red'>
          {errorMessage}
        </Text>
      )}
    </Stack>
  );
};

export default DiscountCodeInput;
